import { makeProtoMetricConfig } from "@/components/charts/chart.utils";
import {
  ComparatorKind,
  FilterKind,
  GroupByKind,
  MetricKind,
  WindowFunctionKind,
} from "@/gen_src/proto/chalk/artifacts/v1/chart_pb";

const makeEqFilter = (kind: FilterKind, value: string[]) => ({
  comparator: ComparatorKind.EQ,
  kind,
  value,
});

const makeOneOfFilter = (kind: FilterKind, value: string[]) => ({
  comparator: ComparatorKind.ONE_OF,
  kind,
  value,
});

const onlineFilter = makeEqFilter(FilterKind.ONLINE_OFFLINE, ["online"]);

const onlineQueryCountFilters = (opts?: { queryName?: string; resourceGroup?: string }) => {
  const filters = [onlineFilter];
  if (opts?.queryName) filters.push(makeEqFilter(FilterKind.QUERY_NAME, [opts.queryName]));
  if (opts?.resourceGroup)
    filters.push(makeEqFilter(FilterKind.RESOURCE_GROUP, [opts.resourceGroup]));
  return filters;
};

// Query-plan latency is emitted by the planner, not the query request path. Its
// series carry query-name and resource-group labels but no online/offline label.
const queryPlanLatencyFilters = (opts?: { queryName?: string; resourceGroup?: string }) => {
  const filters = [];
  if (opts?.queryName) filters.push(makeEqFilter(FilterKind.QUERY_NAME, [opts.queryName]));
  if (opts?.resourceGroup)
    filters.push(makeEqFilter(FilterKind.RESOURCE_GROUP, [opts.resourceGroup]));
  return filters;
};

export const METRIC_CONFIG_REGISTRY = {
  PUBSUB_OLDEST_UNACKED_MESSSAGE_GROUPED: makeProtoMetricConfig({
    name: "Oldest Unacked Message",
    windowPeriod: "1m",
    series: [
      {
        filters: [],
        groupBy: [GroupByKind.SUBSCRIPTION_NAME],
        metric: MetricKind.SUBSCRIPTION_OLDEST_UNACKED_MESSAGE_AGE,
      },
    ],
  }),
  PUBSUB_NUM_UNACKED_MESSAGES_GROUPED: makeProtoMetricConfig({
    name: "Unacked Message Count",
    windowPeriod: "1m",
    series: [
      {
        filters: [],
        groupBy: [GroupByKind.SUBSCRIPTION_NAME],
        metric: MetricKind.SUBSCRIPTION_NUM_UNACKED_MESSAGES,
      },
    ],
  }),
  KAFKA_OFFSET_LAG_GROUPED: makeProtoMetricConfig({
    name: "Offset Lags",
    windowPeriod: "1m",
    series: [
      {
        filters: [],
        groupBy: [GroupByKind.TOPIC_NAME],
        metric: MetricKind.TOPIC_OFFSET_LAG,
      },
    ],
  }),
  KAFKA_MESSAGES_PROCESSED_GROUPED: makeProtoMetricConfig({
    name: "Messages Processed",
    windowPeriod: "1m",
    series: [
      {
        filters: [],
        groupBy: [GroupByKind.TOPIC_NAME],
        metric: MetricKind.TOPIC_MESSAGES_PROCESSED,
        windowFunction: WindowFunctionKind.MAX,
      },
    ],
  }),
  ONLINE_QUERY_COUNT_BY_NAME: makeProtoMetricConfig({
    name: "Online Query Count by Name",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        groupBy: [GroupByKind.QUERY_NAME],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  ONLINE_QUERY_COUNT_BY_RESOURCE_GROUP: makeProtoMetricConfig({
    name: "Online Query Count by Resource Group",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        groupBy: [GroupByKind.RESOURCE_GROUP],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  ONLINE_QUERY_COUNT_FILTERED: (opts?: {
    groupBy?: GroupByKind;
    queryName?: string;
    resourceGroup?: string;
  }) =>
    makeProtoMetricConfig({
      name: "Online Query Count",
      windowPeriod: "1m",
      series: [
        {
          filters: onlineQueryCountFilters(opts),
          groupBy: [opts?.groupBy ?? GroupByKind.QUERY_NAME],
          metric: MetricKind.QUERY_COUNT,
          windowFunction: WindowFunctionKind.COUNT,
        },
      ],
    }),
  ONLINE_QUERY_PLAN_LATENCY_FILTERED: (opts?: {
    groupBy?: GroupByKind;
    queryName?: string;
    resourceGroup?: string;
  }) =>
    makeProtoMetricConfig({
      name: "Plan latency",
      windowPeriod: "1m",
      series: [
        {
          filters: queryPlanLatencyFilters(opts),
          groupBy: [opts?.groupBy ?? GroupByKind.QUERY_NAME],
          metric: MetricKind.QUERY_PLAN_LATENCY,
          windowFunction: WindowFunctionKind.PERCENTILE_95,
        },
      ],
    }),
  ONLINE_QUERY_COUNT_BY_DEPLOYMENT: makeProtoMetricConfig({
    name: "Online query count",
    windowPeriod: "1m",
    series: [
      {
        name: "Queries",
        filters: [onlineFilter],
        groupBy: [GroupByKind.DEPLOYMENT_ID],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  RESOLVER_COUNT_BY_NAME: makeProtoMetricConfig({
    name: "Request count",
    windowPeriod: "1m",
    series: [
      {
        name: "Requests",
        metric: MetricKind.RESOLVER_REQUEST_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
        groupBy: [GroupByKind.RESOLVER_NAME],
        filters: [],
      },
    ],
  }),
  RESOLVER_LATENCY_P95_BY_NAME: makeProtoMetricConfig({
    name: "P95 latency",
    windowPeriod: "1m",
    series: [
      {
        name: "Latency",
        metric: MetricKind.RESOLVER_LATENCY,
        windowFunction: WindowFunctionKind.PERCENTILE_95,
        groupBy: [GroupByKind.RESOLVER_NAME],
        filters: [],
      },
    ],
  }),
  ONLINE_QUERY_COUNT_BY_DEPLOYMENT_AND_STATUS: makeProtoMetricConfig({
    name: "Online Queries",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        groupBy: [GroupByKind.DEPLOYMENT_ID, GroupByKind.QUERY_STATUS],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  ONLINE_QUERY_LATENCY_P95_BY_NAME: makeProtoMetricConfig({
    name: "Online Query Latency P95 by Name",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        name: "Latency",
        groupBy: [GroupByKind.QUERY_NAME],
        metric: MetricKind.QUERY_LATENCY,
        windowFunction: WindowFunctionKind.PERCENTILE_95,
      },
    ],
  }),
  ONLINE_QUERY_LATENCY_PERCENTILES: makeProtoMetricConfig({
    name: "Online Query Latency Percentiles",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        name: "Latency",
        groupBy: [],
        metric: MetricKind.QUERY_LATENCY,
        windowFunction: WindowFunctionKind.ALL_PERCENTILES,
      },
    ],
  }),
  QUERY_SERVER_PROCESS_CPU_UTILIZATION: makeProtoMetricConfig({
    name: "Query Server Process CPU Utilization",
    windowPeriod: "1m",
    series: [
      {
        name: "% CPU",
        metric: MetricKind.CONTAINER_CPU_UTILIZATION,
        groupBy: [GroupByKind.RESOURCE_GROUP, GroupByKind.DEPLOYMENT_ID],
        filters: [onlineFilter],
      },
    ],
  }),
  PROCESS_MEMORY_UTILIZATION: makeProtoMetricConfig({
    name: "Process Memory Utilization",
    windowPeriod: "1m",
    series: [
      {
        name: "Container Memory Used",
        metric: MetricKind.CONTAINER_MEMORY_BYTES,
        groupBy: [GroupByKind.RESOURCE_GROUP, GroupByKind.DEPLOYMENT_ID],
        filters: [onlineFilter],
      },
    ],
  }),
  QUERY_SERVER_REPLICA_COUNT: makeProtoMetricConfig({
    name: "Query Server Replica Count",
    windowPeriod: "1m",
    series: [
      {
        name: "Query Server Replicas",
        metric: MetricKind.REPLICA_COUNT,
        windowFunction: WindowFunctionKind.UNSPECIFIED,
        // Splits replicas by server type (engine / engine-grpc / branch), which is
        // SERVICE_KIND — not a per-operation breakdown.
        groupBy: [GroupByKind.RESOURCE_GROUP, GroupByKind.SERVICE_KIND],
        filters: [onlineFilter],
      },
    ],
  }),
  ONLINE_STORE_KEY_COUNT: makeProtoMetricConfig({
    name: "Online Store Key Count",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        metric: MetricKind.ONLINE_STORE_KEY_COUNT,
        name: "Keys",
        groupBy: [],
      },
    ],
  }),
  ONLINE_STORE_MEMORY_USAGE: makeProtoMetricConfig({
    name: "Online Store Memory Usage",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        metric: MetricKind.ONLINE_STORE_TOTAL_MEMORY,
        name: "Total Memory",
        groupBy: [],
      },
      {
        filters: [onlineFilter],
        metric: MetricKind.ONLINE_STORE_USED_MEMORY,
        name: "Used Memory (RSS)",
        groupBy: [],
      },
      {
        filters: [onlineFilter],
        metric: MetricKind.ONLINE_STORE_ALLOCATED_MEMORY,
        name: "Used Memory (Allocator)",
        groupBy: [],
      },
      {
        filters: [onlineFilter],
        metric: MetricKind.ONLINE_STORE_DATASET_SIZE,
        name: "Dataset Size",
        groupBy: [],
      },
    ],
  }),
  ONLINE_STORE_REQUEST_RATE: makeProtoMetricConfig({
    name: "Online Store Request Rate",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        metric: MetricKind.ONLINE_STORE_REQUESTS_PER_SECOND,
        name: "Requests per Second",
        groupBy: [],
      },
    ],
  }),
  DEPLOYMENTS_PAGE_ONLINE_QUERY_COUNT_BY_DEPLOYMENT: makeProtoMetricConfig({
    name: "Online query count",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        groupBy: [GroupByKind.DEPLOYMENT_ID],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  ONLINE_QUERY_LATENCY_P95_BY_DEPLOYMENT: makeProtoMetricConfig({
    name: "Online query latency P95",
    windowPeriod: "1m",
    series: [
      {
        filters: [onlineFilter],
        name: "Latency",
        groupBy: [GroupByKind.DEPLOYMENT_ID],
        metric: MetricKind.QUERY_LATENCY,
        windowFunction: WindowFunctionKind.PERCENTILE_95,
      },
    ],
  }),
  ONLINE_QUERY_COUNT_TOTAL_5M: makeProtoMetricConfig({
    name: "Online Query Count",
    windowPeriod: "5m",
    series: [
      {
        name: "Queries",
        filters: [onlineFilter],
        groupBy: [],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  ONLINE_QUERY_COUNT_BY_DEPLOYMENT_5M: makeProtoMetricConfig({
    name: "Online Query Count",
    windowPeriod: "5m",
    series: [
      {
        name: "Queries",
        filters: [onlineFilter],
        groupBy: [GroupByKind.DEPLOYMENT_ID],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  ALL_QUERY_COUNT_BY_NAME_5M: makeProtoMetricConfig({
    name: "Online Query Count by Name",
    windowPeriod: "5m",
    series: [
      {
        filters: [],
        groupBy: [GroupByKind.QUERY_NAME],
        metric: MetricKind.QUERY_COUNT,
        windowFunction: WindowFunctionKind.COUNT,
      },
    ],
  }),
  TASKS_CPU_UTILIZATION: makeProtoMetricConfig({
    name: "CPU utilization",
    windowPeriod: "1m",
    series: [
      {
        name: "Process CPU Utilization",
        metric: MetricKind.CPU_UTILIZATION_PERCENT,
        groupBy: [GroupByKind.SCRIPT_TASK_OPERATION_ID],
        filters: [makeEqFilter(FilterKind.COMPUTATION_CONTEXT, ["script_task"])],
      },
    ],
  }),
  TASKS_MEMORY_UTILIZATION: makeProtoMetricConfig({
    name: "Memory utilization",
    windowPeriod: "1m",
    series: [
      {
        name: "Process Memory Utilization",
        metric: MetricKind.MEMORY_USAGE_BYTES,
        groupBy: [GroupByKind.SCRIPT_TASK_OPERATION_ID],
        filters: [makeEqFilter(FilterKind.COMPUTATION_CONTEXT, ["script_task"])],
      },
    ],
  }),
  OFFLINE_QUERY_CPU_UTILIZATION: makeProtoMetricConfig({
    name: "Offline Query CPU Utilization",
    windowPeriod: "1m",
    series: [
      {
        name: "Process CPU Utilization",
        metric: MetricKind.CPU_UTILIZATION_PERCENT,
        groupBy: [GroupByKind.OFFLINE_QUERY_OPERATION_ID],
        filters: [makeEqFilter(FilterKind.COMPUTATION_CONTEXT, ["offline_query"])],
      },
    ],
  }),
  OFFLINE_QUERY_MEMORY_UTILIZATION: makeProtoMetricConfig({
    name: "Offline Query Memory Utilization",
    windowPeriod: "1m",
    series: [
      {
        name: "Process Memory Utilization",
        metric: MetricKind.MEMORY_USAGE_BYTES,
        groupBy: [GroupByKind.OFFLINE_QUERY_OPERATION_ID],
        filters: [makeEqFilter(FilterKind.COMPUTATION_CONTEXT, ["offline_query"])],
      },
    ],
  }),
  SCHEDULED_RUN_DURATION_BY_RESOLVER: makeProtoMetricConfig({
    name: "Scheduled Run Duration by Resolver",
    windowPeriod: "5m",
    series: [
      {
        name: "Duration",
        filters: [],
        groupBy: [GroupByKind.RESOLVER_NAME],
        metric: MetricKind.CRON_LATENCY,
        windowFunction: WindowFunctionKind.MEAN,
      },
    ],
  }),
  QUERY_COUNT_FILTERED_BY_QUERY_NAME: (queryName: string) =>
    makeProtoMetricConfig({
      name: "Query Count (5m)",
      windowPeriod: "5m",
      series: [
        {
          filters: [makeEqFilter(FilterKind.QUERY_NAME, [queryName])],
          groupBy: [],
          metric: MetricKind.QUERY_COUNT,
          windowFunction: WindowFunctionKind.COUNT,
        },
      ],
    }),
  QUERY_LATENCY_MEAN_FILTERED_BY_QUERY_NAME: (queryName: string) =>
    makeProtoMetricConfig({
      name: "Query Latency Mean (5m)",
      windowPeriod: "5m",
      series: [
        {
          filters: [makeEqFilter(FilterKind.QUERY_NAME, [queryName])],
          groupBy: [],
          metric: MetricKind.QUERY_LATENCY,
          windowFunction: WindowFunctionKind.MEAN,
        },
      ],
    }),
  QUERY_COUNT_BY_QUERY_NAMES: (queryNames: string[]) =>
    makeProtoMetricConfig({
      name: "Queries producing this feature",
      windowPeriod: "1m",
      series: [
        {
          metric: MetricKind.QUERY_COUNT,
          groupBy: [GroupByKind.QUERY_NAME],
          windowFunction: WindowFunctionKind.COUNT,
          filters: [makeOneOfFilter(FilterKind.QUERY_NAME, queryNames)],
        },
      ],
    }),
  QUERY_LATENCY_P95_BY_QUERY_NAMES: (queryNames: string[]) =>
    makeProtoMetricConfig({
      name: "Query latency P95 producing this feature",
      windowPeriod: "1m",
      series: [
        {
          metric: MetricKind.QUERY_LATENCY,
          groupBy: [GroupByKind.QUERY_NAME],
          windowFunction: WindowFunctionKind.PERCENTILE_95,
          filters: [makeOneOfFilter(FilterKind.QUERY_NAME, queryNames)],
        },
      ],
    }),
  RESOLVER_RUN_COUNT_BY_RESOLVER_NAMES: (resolverFqns: string[]) =>
    makeProtoMetricConfig({
      name: "Resolver run count",
      windowPeriod: "1m",
      series: [
        {
          metric: MetricKind.RESOLVER_REQUEST_COUNT,
          groupBy: [GroupByKind.RESOLVER_NAME],
          windowFunction: WindowFunctionKind.COUNT,
          filters: [makeOneOfFilter(FilterKind.RESOLVER_NAME, resolverFqns)],
        },
      ],
    }),
  RESOLVER_LATENCY_P95_BY_RESOLVER_NAMES: (resolverFqns: string[]) =>
    makeProtoMetricConfig({
      name: "Resolver latency P95",
      windowPeriod: "1m",
      series: [
        {
          metric: MetricKind.RESOLVER_LATENCY,
          groupBy: [GroupByKind.RESOLVER_NAME],
          windowFunction: WindowFunctionKind.PERCENTILE_95,
          filters: [makeOneOfFilter(FilterKind.RESOLVER_NAME, resolverFqns)],
        },
      ],
    }),
  FEATURE_REQUEST_COUNT_BY_FEATURE_NAMES: (features: string[]) =>
    makeProtoMetricConfig({
      name: "Feature Request Count",
      windowPeriod: "1m",
      series: [
        {
          metric: MetricKind.FEATURE_REQUEST_COUNT,
          groupBy: [GroupByKind.FEATURE_NAME],
          windowFunction: WindowFunctionKind.COUNT,
          filters: [makeOneOfFilter(FilterKind.FEATURE_NAME, features)],
        },
      ],
    }),
  FEATURE_LATENCY_P95_BY_FEATURE_NAMES: (features: string[]) =>
    makeProtoMetricConfig({
      name: "Feature Latency P95",
      windowPeriod: "1m",
      series: [
        {
          metric: MetricKind.FEATURE_LATENCY,
          groupBy: [GroupByKind.FEATURE_NAME],
          windowFunction: WindowFunctionKind.PERCENTILE_95,
          filters: [makeOneOfFilter(FilterKind.FEATURE_NAME, features)],
        },
      ],
    }),
  REQUEST_COUNT_BY_STATUS_FILTERED_BY_RESOLVER_NAME: (resolverFqn: string) =>
    makeProtoMetricConfig({
      name: "Request Count by Status",
      windowPeriod: "1m",
      series: [
        {
          name: "Requests",
          metric: MetricKind.RESOLVER_REQUEST_COUNT,
          groupBy: [GroupByKind.RESOLVER_STATUS],
          windowFunction: WindowFunctionKind.SUM,
          filters: [makeEqFilter(FilterKind.RESOLVER_NAME, [resolverFqn])],
        },
      ],
    }),
  RESOLVER_LATENCY_FOR_RESOLVER_3H: (resolverFqn: string, resolverName: string) =>
    makeProtoMetricConfig({
      name: `Resolver Latency for ${resolverName}`,
      windowPeriod: "3h",
      series: [
        {
          name: "P95",
          metric: MetricKind.RESOLVER_LATENCY,
          windowFunction: WindowFunctionKind.PERCENTILE_95,
          filters: [makeEqFilter(FilterKind.RESOLVER_NAME, [resolverFqn])],
          groupBy: [],
        },
      ],
    }),
} as const;
